/**
 * タイトル画面の「キー操作ファースト」(docs/15_game_feel.md §1-2 / §2-A)。
 *
 * Typing Land はメニューまで全部キーで動く。PaLTown も Enter =「はじめる」、
 * 数字キー = その他の入口、にする。ボタン側には同じ文字のキーバッジを添える。
 *
 * **マウス/タップは今までどおり**。キーは追加の近道であって、置き換えではない。
 * 実際のイベント購読は `ui/components/gameFeel.tsx` の `useHotkey` / `useChoiceKeys`
 * に任せ、ここは「どのキーがどこへ行くか」の表だけを持つ(単体テストできる形)。
 */

/** タイトルから行ける先。`gameStore` の `go*` に1対1で対応する。 */
export type TitleAction = 'start' | 'puzzle' | 'learn' | 'gacha' | 'avatar' | 'myPage' | 'pal';

/** 数字キー(ボタンの並び順)。`useChoiceKeys` にそのまま渡す。 */
export const TITLE_DIGIT_KEYS = ['1', '2', '3', '4', '5', '6'] as const;

/** `TITLE_DIGIT_KEYS` と同じ並び。`useChoiceKeys` の index で引く。 */
export const TITLE_DIGIT_ACTIONS = [
  'puzzle',
  'learn',
  'gacha',
  'avatar',
  'myPage',
  'pal',
] as const satisfies readonly TitleAction[];

/**
 * ボタンに出すキーバッジの文字。
 * バッジは `aria-hidden` で出すこと(アクセシブル名が変わると E2E のボタン名が壊れる)。
 */
export const TITLE_KEY_BADGES: Readonly<Record<TitleAction, string>> = {
  start: 'Enter',
  puzzle: '1',
  learn: '2',
  gacha: '3',
  avatar: '4',
  myPage: '5',
  pal: '6',
};

/** `KeyboardEvent.key` を行き先に読み替える。関係ないキーは `null`。 */
export function titleActionForKey(key: string): TitleAction | null {
  if (key === 'Enter') return 'start';
  const index = (TITLE_DIGIT_KEYS as readonly string[]).indexOf(key);
  return index < 0 ? null : (TITLE_DIGIT_ACTIONS[index] ?? null);
}

/**
 * その要素にフォーカスがあるあいだ、ロゴのタイルタイピングを止めるか。
 *
 * 文字を打つ場所(入力欄・セレクト・contenteditable)ではロゴ遊びが邪魔になる。
 */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
